import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Edit, X, Calendar, Clock, FileText, User } from "lucide-react";
import { toast } from "sonner";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import {
  useGetMaintenanceRequestQuery,
  useSendEmailMutation,
  useUpdatePreventiveMaintenanceMutation,
} from "../../slices/maintenanceApiSlice";
import { useGetAssetQuery } from "../../slices/assetApiSlice";
import {
  useGetUserByEmailQuery,
  useGetAllUsersQuery,
  useGetDepartmentQuery,
} from "../../slices/userApiSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PMaintenance = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setSelectedWorkStatus(status);
    } else {
      setSelectedWorkStatus("");
    }
  }, [searchParams]);
  const [editModalData, setEditModalData] = useState(null);
  const [userID, setUserID] = useState(null);
  const [supervisor, setEmail] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusPending, setStatusPending] = useState(null);
  const rowsPerPage = 10;

  const {
    data: maintenanceRequest,
    isLoading,
    refetch: refetchMaintenanceRequest,
  } = useGetMaintenanceRequestQuery();
  const [updatePreventiveMaintenance] =
    useUpdatePreventiveMaintenanceMutation();

  const { data: assetData, refetch: refetchAssetData } = useGetAssetQuery();
  const { data: allUsers } = useGetAllUsersQuery();
  const { data: allDepartment } = useGetDepartmentQuery();
  const [sendEmail] = useSendEmailMutation();

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || "",
  );

  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  const [data, setData] = useState([]);

  useEffect(() => {
    if (maintenanceRequest && userByEmial?.user?.academyId) {
      const filtered = maintenanceRequest
        .filter((item) => item.academyId === userByEmial.user.academyId)
        .map((item) => {
          // Look up asset name from assetData by assetCode
          const asset = assetData?.find((a) => a.assetCode === item.assetCode);
          // Look up supervisor name from allUsers by supervisorEmail or userID
          const supervisorUser = (allUsers || []).find(
            (u) =>
              (item.supervisorEmail && u.email === item.supervisorEmail) ||
              (item.userID && (String(u.userId) === String(item.userID) || String(u.id) === String(item.userID) || String(u._id) === String(item.userID)))
          );
          const supervisorDisplay =
            supervisorUser?.name || item.supervisorEmail || "-";
          return {
            ...item,
            assetName: asset?.title || asset?.name || item.assetCode,
            userEmail: supervisorDisplay,
            supervisorEmail: item.supervisorEmail || supervisorUser?.email,
            userID: item.userID || supervisorUser?.userId || supervisorUser?._id || supervisorUser?.id,
          };
        })
        .sort(
          (a, b) =>
            new Date(b.startDate || b.requestDate) -
            new Date(a.startDate || a.requestDate),
        ); // Added sort by startDate/requestDate desc

      setData(filtered);
    }
  }, [maintenanceRequest, userByEmial, assetData, allUsers]);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading maintenance...", { id: "loading-maintenance" });
    } else {
      toast.dismiss("loading-maintenance");
    }
  }, [isLoading]);

  const getWorkOrderStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "inprogress":
      case "in progress":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      case "completed":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };


  const sortedData = [...data];
  const filteredData = sortedData.filter((item) => {
    const matchesSearch = Object.values(item).some(
      (value) =>
        value != null &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const matchesWorkStatus =
      selectedWorkStatus === "" ||
      item.status?.toLowerCase() === selectedWorkStatus.toLowerCase();

    return matchesSearch && matchesWorkStatus;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  console.log("Displayed Data:", displayedData);

  const handleEditRow = (item) => {
    console.log("Edit modal data:", item);
    console.log("Workers list:", workersList);
    setEditModalData(item);
    setStatusPending(item?.status);
  };

  const handleSaveEdit = async () => {
    if (!editModalData) return;

    setIsSaving(true);

    try {
      const payload = {
        id: editModalData.maintenanceID,
        maintenance: {
          description: editModalData.description,
          startDate: editModalData.startDate,
          timeStart: editModalData.timeStart,
          repeat: editModalData.repeat,
          endDate: editModalData.endDate,
          status: editModalData.status || "pending",
          userID: userID || editModalData.userID,
          supervisorEmail: supervisor || editModalData.supervisorEmail,
        },
      };

      const response = await updatePreventiveMaintenance(payload).unwrap();

      if (supervisor) {
        await sendEmail({ to: supervisor }).unwrap();
      }

      setUserID(null);
      setEmail(null);

      setData((prevData) =>
        prevData.map((item) =>
          item.maintenanceID === payload.id
            ? { ...item, ...payload.maintenance }
            : item,
        ),
      );
      toast.success("Record Updated", {
        description: "Maintenance record updated and email sent successfully.",
      });

      setEditModalData(null);
    } catch (err) {
      toast.error("Update Failed", {
        description:
          "We couldn't update the maintenance record. Please try again shortly.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Show all supervisors from the same academy
  const workersList = (allUsers || [])
    .filter((user) => {
      const matchesAcademy =
        user?.academyId?.trim().toLowerCase() ===
        userByEmial?.user?.academyId?.trim().toLowerCase();
      const roleName =
        typeof user.role === "string" ? user.role : user.role?.name;
      const isSupervisor = roleName?.toLowerCase() === "supervisor";
      return matchesAcademy && isSupervisor;
    })
    .map((user) => {
      console.log("User object:", user); // Debug log to see user structure
      return {
        value: user.userId || user._id || user.id,
        label: user.name || user.email,
        email: user.email,
      };
    });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Preventive Maintenance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and schedule preventive maintenance tasks and schedules
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search maintenance..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Asset Code</TableHead>
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Schedule(month)</TableHead>
                <TableHead className="font-semibold">From Date</TableHead>
                <TableHead className="font-semibold">To Date</TableHead>
                <TableHead className="font-semibold">Assign to</TableHead>
                <TableHead className="font-semibold">Work Status</TableHead>
                <TableHead className="font-semibold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow 
                    key={item.maintenanceID || index}
                    className="hover:bg-[#305845]/5 transition-colors cursor-pointer"
                    onClick={() => handleEditRow(item)}
                  >
                    <TableCell>{item.assetCode || "-"}</TableCell>
                    <TableCell>{item.assetName || "-"}</TableCell>
                    <TableCell>{item.description || "-"}</TableCell>
                    <TableCell>{item.repeat || "-"}</TableCell>
                    <TableCell>{formatDate(item.startDate)}</TableCell>
                    <TableCell>{formatDate(item.endDate)}</TableCell>
                    <TableCell>{item.supervisorEmail || "-"}</TableCell>
                    <TableCell>
                      <Badge className={getWorkOrderStatusBadge(item.status)}>
                        {item.status || "-"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRow(item)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No maintenance records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="border-t bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing{" "}
            <span className="font-medium text-foreground">
              {displayedData.length > 0
                ? (currentPage - 1) * rowsPerPage + 1
                : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium text-foreground">
              {Math.min(currentPage * rowsPerPage, filteredData.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-foreground">
              {filteredData.length}
            </span>{" "}
            results
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog
        open={!!editModalData}
        onOpenChange={(open) => !open && setEditModalData(null)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preventive Maintenance Schedule Form</DialogTitle>
            <DialogDescription>
              Update preventive maintenance schedule details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Maintenance Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Maintenance Details
              </h3>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  type="text"
                  value={editModalData?.description || ""}
                  onChange={(e) =>
                    setEditModalData({
                      ...editModalData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter maintenance description..."
                />
              </div>

              <div className="space-y-2">
                <Label>Assign Supervisor</Label>
                <Select
                  value={
                    editModalData?.userID ? String(editModalData.userID) : ""
                  }
                  onValueChange={(value) => {
                    console.log("Selected value:", value);
                    const selected = workersList.find(
                      (worker) => String(worker.value) === value,
                    );
                    console.log("Found worker:", selected);
                    if (selected) {
                      setEditModalData({
                        ...editModalData,
                        userID: selected.value,
                        userEmail: selected.email,
                      });
                      setUserID(selected.value);
                      setEmail(selected.email);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supervisor" />
                  </SelectTrigger>
                  <SelectContent>
                    {workersList.length > 0 ? (
                      workersList.map((worker) => (
                        <SelectItem
                          key={worker.value}
                          value={String(worker.value)}
                        >
                          {worker.label}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-options" disabled>
                        No supervisors found in this academy
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Schedule Section */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Schedule Maintenance Notification
              </h3>

              {/* From Date and Schedule Time - Side by Side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={editModalData?.startDate || ""}
                    onChange={(e) =>
                      setEditModalData({
                        ...editModalData,
                        startDate: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Schedule Time</Label>
                  <Input
                    type="time"
                    value={editModalData?.timeStart || ""}
                    onChange={(e) =>
                      setEditModalData({
                        ...editModalData,
                        timeStart: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              {/* Repeats and To Date - Side by Side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Repeats</Label>
                  <Input
                    type="text"
                    value={editModalData?.repeat || ""}
                    onChange={(e) =>
                      setEditModalData({
                        ...editModalData,
                        repeat: e.target.value,
                      })
                    }
                    placeholder="e.g., Daily, Weekly, Monthly"
                  />
                </div>

                <div className="space-y-2">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={editModalData?.endDate || ""}
                    onChange={(e) =>
                      setEditModalData({
                        ...editModalData,
                        endDate: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setEditModalData(null)}>
              Close
            </Button>
            {statusPending === "Pending" && (
              <Button
                disabled={isSaving}
                onClick={handleSaveEdit}
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PMaintenance;
